import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/pt-br";
import {
  TbArrowLeft,
  TbArrowsLeftRight,
  TbCheck,
  TbChevronDown,
  TbCopy,
  TbDownload,
  TbPrinter,
  TbShare2,
  TbX,
} from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import { CompanyLogo } from "../../Atoms";
import { formatCurrency } from "../../../Utils/formatters";
import { useBalance } from "../../../Hooks/useBalance";
import { useReceiptDownload } from "../ReceiptModal/useReceiptDownload";
import { useReceiptPrint } from "../ReceiptModal/useReceiptPrint";
import { useToast } from "../../../Hooks/toast";
import { PageHeaderWithBack } from "../PageHeaderWithBack";

moment.locale("pt-br");

const findDetailValue = (details, sectionName, fieldName) => {
  const section = details?.find(
    (item) => item.value?.toLowerCase() === sectionName.toLowerCase()
  );
  const row = section?.items?.find(
    (item) => item.title?.toLowerCase() === fieldName.toLowerCase()
  );
  return row?.value || "";
};

const formatAuthValue = (value) => {
  if (!value || value.length <= 24) return value;
  return `${value.slice(0, 10)}…${value.slice(-10)}`;
};

const DetailRow = ({ label, value, mono = false, onCopy }) => {
  if (!value) return null;

  return (
    <div className="pix-transfer-final__row">
      <span className="pix-transfer-final__label">{label}</span>
      <span className="flex min-w-0 items-start gap-1.5">
        <span
          className={`pix-transfer-final__value ${mono ? "font-mono text-xs" : ""}`}
          title={value}
        >
          {mono ? formatAuthValue(value) : value}
        </span>
        {onCopy && (
          <button
            type="button"
            className="btn-outline-icon shrink-0"
            onClick={onCopy}
            aria-label={`Copiar ${label}`}
            title="Copiar"
          >
            <TbCopy className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
};

const DetailPanel = ({ title, items = [], authValue, onCopyAuth }) => {
  const visibleItems = items.filter((item) => item?.value);
  if (!visibleItems.length) return null;

  return (
    <div className="pix-transfer-final__panel">
      <h3 className="pix-transfer-final__panel-title">{title}</h3>
      <div className="space-y-1.5">
        {visibleItems.map((item, index) => {
          const isAuth =
            item.title?.toLowerCase() === "autenticação" ||
            item.title?.toLowerCase() === "id da operação";
          return (
            <DetailRow
              key={`${item.title}-${index}`}
              label={item.title}
              value={item.value}
              mono={isAuth}
              onCopy={isAuth && authValue ? onCopyAuth : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export const FinalStep = ({
  transactionStatus,
  transactionData,
  onClose,
  onNewTransfer,
  inModal = false,
  sourceAccountLabel,
  sourceAccountBalance,
  sourceAccountBalanceLoading,
  hideToolbar = false,
}) => {
  const { getBalance: refreshBalance } = useBalance();
  const { addToast } = useToast();
  const { downloadPdf, loadingDownload } = useReceiptDownload(transactionData, {
    fileName: "comprovante-transferencia.pdf",
  });
  const { printRef, print } = useReceiptPrint();
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (transactionStatus === true) {
      refreshBalance();
    }
  }, [transactionStatus, refreshBalance]);

  const sections = transactionData?.details || [];

  const {
    destinatarioSection,
    transferenciaSection,
    recipientName,
    recipientDocument,
    recipientInstitution,
    operationId,
    operationType,
    description,
  } = useMemo(() => {
    const destinatario = sections.find(
      (item) => item.value?.toLowerCase() === "destinatário"
    );
    const transferencia = sections.find(
      (item) => item.value?.toLowerCase() === "transferência"
    );
    const name =
      findDetailValue(sections, "Destinatário", "Favorecido") ||
      transactionData?.target?.entity?.name ||
      transactionData?.target?.name ||
      "";
    const document =
      findDetailValue(sections, "Destinatário", "Documento") ||
      transactionData?.target?.entity?.document ||
      "";
    const institution =
      findDetailValue(sections, "Destinatário", "Instituição") || "Valut (000)";
    const opId =
      findDetailValue(sections, "Transferência", "ID da operação") ||
      transactionData?.id ||
      transactionData?.autentication ||
      "";
    const tipo =
      findDetailValue(sections, "Transferência", "Tipo") || "Entre contas Valut";
    const desc =
      findDetailValue(sections, "Transferência", "Descrição") ||
      transactionData?.description ||
      "";

    return {
      destinatarioSection: destinatario,
      transferenciaSection: transferencia,
      recipientName: name,
      recipientDocument: document,
      recipientInstitution: institution,
      operationId: opId,
      operationType: tipo,
      description: desc,
    };
  }, [sections, transactionData]);

  const handleCopyAuth = useCallback(async () => {
    if (!operationId) return;
    try {
      await navigator.clipboard.writeText(operationId);
      addToast({
        title: "Copiado",
        description: "ID da operação copiado.",
        type: "success",
      });
    } catch {
      addToast({
        title: "Não foi possível copiar",
        description: "Copie manualmente o ID da operação.",
        type: "error",
      });
    }
  }, [operationId, addToast]);

  const handleShare = useCallback(async () => {
    const amountLabel = formatCurrency(transactionData?.amount);
    const dateLabel = transactionData?.date
      ? moment(transactionData.date).format("DD/MM/YYYY [às] HH:mm")
      : "";
    const shareText = [
      "Transferência entre contas Valut",
      `Valor: ${amountLabel}`,
      recipientName ? `Para: ${recipientName}` : null,
      dateLabel ? `Data: ${dateLabel}` : null,
      operationId ? `ID: ${operationId}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Comprovante de transferência — Valut",
          text: shareText,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      addToast({
        title: "Copiado",
        description: "Resumo do comprovante copiado para a área de transferência.",
        type: "success",
      });
    } catch {
      addToast({
        title: "Não foi possível compartilhar",
        description: "Tente baixar o PDF ou imprimir o comprovante.",
        type: "error",
      });
    }
  }, [transactionData, recipientName, operationId, addToast]);

  const handleRetry = onNewTransfer || onClose;

  if (!transactionStatus) {
    const failureContent = (
      <section className="app-card p-5 text-center sm:p-6">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
          <TbX className="h-5 w-5 text-red-500" />
        </div>
        <h2 className="mt-3 text-base font-bold text-brand-dark">
          Transferência não concluída
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Não foi possível finalizar a transferência. Tente novamente ou volte para
          revisar os dados.
        </p>
        {!inModal && (
          <button type="button" className="btn-outline mt-4" onClick={handleRetry}>
            <TbArrowLeft className="h-4 w-4" />
            Tentar novamente
          </button>
        )}
      </section>
    );

    if (inModal) {
      return <div className="pix-transfer-final w-full min-w-0">{failureContent}</div>;
    }

    return (
      <div className="w-full min-w-0">
        <PageHeaderWithBack
          title="Transferir entre contas"
          subTitle="Não foi possível concluir a transferência."
          onBack={onClose}
        />
        <div className="mt-4">{failureContent}</div>
      </div>
    );
  }

  const formattedLongDate = transactionData?.date
    ? moment(transactionData.date).format("dddd, D [de] MMMM [de] YYYY [às] HH:mm")
    : "—";

  const formattedShortDate = transactionData?.date
    ? moment(transactionData.date).format("DD/MM/YYYY [às] HH:mm")
    : "—";

  const amountLabel = formatCurrency(transactionData?.amount);
  const recipientMeta = [recipientInstitution, recipientDocument]
    .filter(Boolean)
    .join(" · ");

  const transferenciaItems =
    transferenciaSection?.items ||
    [
      operationType && { title: "Tipo", value: operationType },
      operationId && { title: "ID da operação", value: operationId },
      description && { title: "Descrição", value: description },
    ].filter(Boolean);

  const receiptBody = (
    <div className="pix-transfer-final space-y-4">
      {!hideToolbar && !inModal && (
        <div className="pix-transfer-final__toolbar">
          <button
            type="button"
            className="pix-transfer-final__back-link"
            onClick={onClose}
          >
            <TbArrowLeft className="h-4 w-4 shrink-0" />
            Voltar
          </button>
          <button type="button" className="btn-primary shrink-0" onClick={handleRetry}>
            <TbArrowsLeftRight className="h-4 w-4" />
            Nova transferência
          </button>
        </div>
      )}

      <div ref={printRef} className="space-y-4">
        <section className="app-card pix-transfer-final__receipt overflow-hidden">
          <div className="pix-transfer-final__receipt-head">
            <div className="mb-4 flex justify-center">
              <CompanyLogo forceValut width={140} height={36} />
            </div>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <TbCheck className="h-6 w-6 text-emerald-600" strokeWidth={2.5} />
            </div>
            {!inModal && (
              <h2 className="mt-4 text-base font-bold text-brand-dark">
                Transferência realizada
              </h2>
            )}
            <p className={`${inModal ? "mt-4" : "mt-1"} text-sm capitalize text-gray-500`}>
              {formattedLongDate}
            </p>
            <p className="mt-4 text-3xl font-bold tracking-tight text-brand-dark">
              {amountLabel}
            </p>
            {recipientName && (
              <p className="mt-3 text-sm text-gray-600">
                Para:{" "}
                <strong className="font-semibold text-brand-dark">{recipientName}</strong>
              </p>
            )}
            {recipientMeta && (
              <p className="mt-1 text-xs text-gray-500">{recipientMeta}</p>
            )}
          </div>

          {(sourceAccountLabel || sourceAccountBalance != null) && (
            <div className="pix-transfer-final__balance-strip">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-xs font-medium text-emerald-800">
                  Saldo após envio
                </span>
                <span className="text-[11px] text-emerald-700/80">
                  {sourceAccountLabel || "Conta"}
                </span>
              </div>
              <span className="shrink-0 text-base font-bold text-emerald-900">
                {sourceAccountBalanceLoading ? (
                  <CircularProgress size={16} sx={{ color: "#059669" }} />
                ) : (
                  sourceAccountBalance || "—"
                )}
              </span>
            </div>
          )}
        </section>

        <div className="pix-transfer-final__actions">
          <button type="button" className="btn-primary" onClick={handleShare}>
            <TbShare2 className="h-4 w-4" />
            Compartilhar
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={downloadPdf}
            disabled={loadingDownload}
          >
            <TbDownload className="h-4 w-4" />
            {loadingDownload ? "Gerando PDF…" : "Baixar PDF"}
          </button>
          <button type="button" className="btn-outline" onClick={print}>
            <TbPrinter className="h-4 w-4" />
            Imprimir
          </button>
        </div>

        <div className="pix-transfer-final__summary-bar">
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">Data e hora</span>
            <span className="pix-transfer-final__summary-value">{formattedShortDate}</span>
          </div>
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">ID operação</span>
            <span
              className="pix-transfer-final__summary-value font-mono text-xs"
              title={operationId || undefined}
            >
              {formatAuthValue(operationId) || "—"}
            </span>
          </div>
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">Tipo operação</span>
            <span className="pix-transfer-final__summary-value">{operationType}</span>
          </div>
        </div>

        <div className="pix-transfer-final__accordion app-card overflow-hidden">
          <button
            type="button"
            className="pix-transfer-final__accordion-trigger"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
          >
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold text-brand-dark">Detalhes</span>
              <span className="pix-transfer-final__badge">Concluído</span>
            </span>
            <TbChevronDown
              className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                detailsOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {detailsOpen && (
            <div className="pix-transfer-final__accordion-body">
              <div className="grid gap-4 md:grid-cols-2">
                <DetailPanel
                  title="Destinatário"
                  items={
                    destinatarioSection?.items ||
                    [
                      recipientName && { title: "Favorecido", value: recipientName },
                      recipientDocument && {
                        title: "Documento",
                        value: recipientDocument,
                      },
                      recipientInstitution && {
                        title: "Instituição",
                        value: recipientInstitution,
                      },
                    ].filter(Boolean)
                  }
                />
                <DetailPanel
                  title="Transferência"
                  items={transferenciaItems}
                  authValue={operationId}
                  onCopyAuth={handleCopyAuth}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="pix-transfer-final__footer">
        Comprovante oficial · Transação realizada via Valut
        {transactionData?.supportPhone ? ` · ${transactionData.supportPhone}` : ""}
      </p>
    </div>
  );

  if (inModal) {
    return <div className="w-full min-w-0">{receiptBody}</div>;
  }

  return (
    <div className="w-full min-w-0">
      <PageHeaderWithBack
        title="Comprovante de transferência"
        subTitle="Entre contas Valut."
        onBack={onClose}
      />
      <div className="mt-4">{receiptBody}</div>
    </div>
  );
};
